
import { useState, useEffect, useCallback } from 'react'
import { getSession, $fetch } from '@/lib/auth-client'
import { toast } from '@/components/ui/toast'
import { describeInviteError } from '@/lib/api/invite-errors'

export interface InviteLink {
  id: string
  url: string
  linkSlug: string
  /** Serialized `Date` from the API. */
  createdAt: string
}

/**
 * One accepted invite, as `InviteDashboardData` in @genieartinc/core-ports defines it.
 * The invitee is deliberately not named: the referrer sees the code they shared, not the person.
 */
export interface Referral {
  id: string
  inviteCode: string
  /** Invite type, e.g. LINK or EMAIL. */
  source: string
  /** Invite status as the database spells it, e.g. ACCEPTED. */
  status: string
  /** What this invitee has earned the referrer so far, in credits. */
  earnings: number
  createdAt: string
}

interface InviteDashboardStats {
  totalInvites: number
  totalAccepted: number
  totalEarnings: number
}

interface InviteDashboard {
  links: InviteLink[]
  referrals: Referral[]
  stats: InviteDashboardStats
}

interface UseInvitesReturn {
  dashboard: InviteDashboard | null
  loading: boolean
  error: string | null
  createLink: () => Promise<InviteLink | null>
  sendEmailInvites: (emails: string[], message?: string) => Promise<{ sent: number; failed: string[] } | null>
  revokeInvite: (id: string) => Promise<boolean>
  refresh: () => Promise<void>
}

/**
 * The shape every api-app route answers with. The payload lives under `data`; reading the
 * top level instead is what made this panel look empty while the writes were succeeding.
 */
interface ApiEnvelope<T> {
  success?: boolean
  data?: T
  error?: string
  code?: string
}

interface RequestResult<T> {
  ok: boolean
  data: T | null
  /** Copy for the user, already resolved from the error code. Null when the call succeeded. */
  error: string | null
}

const EMPTY_STATS: InviteDashboardStats = { totalInvites: 0, totalAccepted: 0, totalEarnings: 0 }

/**
 * Call an invite endpoint and unwrap its envelope.
 *
 * better-fetch does not throw on a non-2xx: it reports `{ data: null, error }`, where `error`
 * is the parsed body plus `status`. A transport failure looks the same but carries no `code`,
 * so both paths end up as a message rather than a silent success.
 */
const requestInviteApi = async <T,>(
  url: string,
  fallbackMessage: string,
  options: RequestInit = {},
): Promise<RequestResult<T>> => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
  if (!apiBaseUrl) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }
  const fullUrl = url.startsWith('/') ? `${apiBaseUrl}${url}` : url

  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) }

  // Only set Content-Type and body when there's actual body content
  const fetchOptions: { method: string; headers: Record<string, string>; body?: BodyInit | null } = {
    method: options.method || 'GET',
    headers,
  }

  if (options.body) {
    fetchOptions.headers['Content-Type'] = 'application/json'
    fetchOptions.body = options.body
  }

  const response = await $fetch(fullUrl, fetchOptions) as {
    data: ApiEnvelope<T> | null
    error: (ApiEnvelope<T> & { message?: string }) | null
  }

  if (response.error) {
    const { code, error, message } = response.error
    return { ok: false, data: null, error: describeInviteError(code, error || message, fallbackMessage) }
  }

  const body = response.data
  if (!body || body.success === false) {
    return { ok: false, data: null, error: describeInviteError(body?.code, body?.error, fallbackMessage) }
  }

  return { ok: true, data: body.data ?? null, error: null }
}

export function useInvites(): UseInvitesReturn {
  const [dashboard, setDashboard] = useState<InviteDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const session = await getSession()
      if (!session?.data) {
        throw new Error('No session found')
      }

      const result = await requestInviteApi<InviteDashboard>(
        '/api/invite/dashboard',
        'Failed to fetch invite dashboard',
      )

      if (!result.ok) {
        throw new Error(result.error ?? 'Failed to fetch invite dashboard')
      }

      setDashboard({
        links: result.data?.links ?? [],
        referrals: result.data?.referrals ?? [],
        stats: result.data?.stats ?? EMPTY_STATS,
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('Error fetching invite dashboard:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createLink = async (): Promise<InviteLink | null> => {
    try {
      const result = await requestInviteApi<InviteLink>('/api/invite/link', 'Failed to create invite link', {
        method: 'POST',
      })

      if (result.ok && result.data) {
        await fetchDashboard()
        toast.success('Invite link created')
        return result.data
      }

      toast.error(result.error ?? 'Failed to create invite link')
      return null
    } catch (err) {
      console.error('Error creating invite link:', err)
      toast.error('An unexpected error occurred')
      return null
    }
  }

  const sendEmailInvites = async (
    emails: string[],
    message?: string,
  ): Promise<{ sent: number; failed: string[] } | null> => {
    try {
      const result = await requestInviteApi<{ sent: number; failed?: string[] }>(
        '/api/invite/email',
        'Failed to send email invites',
        { method: 'POST', body: JSON.stringify({ emails, message }) },
      )

      if (result.ok && result.data && typeof result.data.sent === 'number') {
        await fetchDashboard()
        const failed = result.data.failed ?? []
        if (failed.length > 0) {
          toast.error(`${failed.length} invite(s) failed to send`)
        }
        return { sent: result.data.sent, failed }
      }

      toast.error(result.error ?? 'Failed to send email invites')
      return null
    } catch (err) {
      console.error('Error sending email invites:', err)
      toast.error('An unexpected error occurred')
      return null
    }
  }

  const revokeInvite = async (id: string): Promise<boolean> => {
    try {
      // Revoke answers `{ success: true }` with no payload, so `ok` is the whole result
      const result = await requestInviteApi<never>(`/api/invite/${id}`, 'Failed to revoke invite', {
        method: 'DELETE',
      })

      if (result.ok) {
        await fetchDashboard()
        toast.success('Invite revoked')
        return true
      }

      toast.error(result.error ?? 'Failed to revoke invite')
      return false
    } catch (err) {
      console.error('Error revoking invite:', err)
      toast.error('An unexpected error occurred')
      return false
    }
  }

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  return {
    dashboard,
    loading,
    error,
    createLink,
    sendEmailInvites,
    revokeInvite,
    refresh: fetchDashboard,
  }
}
